import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { fetchHistory, type HistoryEntry } from '../api/me';
import { formatDateUK } from '../lib/format';
import { londonToday } from '../lib/gameDate';
import type { Difficulty } from '../api/puzzles';

const DIFFICULTIES: Difficulty[] = ['EASY', 'MEDIUM', 'HARD'];
const FILTERS: Array<{ key: Difficulty | 'ALL'; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'EASY', label: 'Easy' },
  { key: 'MEDIUM', label: 'Medium' },
  { key: 'HARD', label: 'Hard' },
];

// Entries per fetch; days group client-side, so ~20 days per page.
const PAGE_SIZE = 60;

type DayOutcome = 'won' | 'lost' | 'in_progress' | 'not_played';

function dayOutcome(entries: HistoryEntry[]): DayOutcome {
  if (entries.length === 0) return 'not_played';
  if (entries.some((e) => e.won)) return 'won';
  if (entries.filter((e) => e.status === 'LOST').length >= 3) return 'lost';
  return 'in_progress';
}

const OUTCOME_BADGES: Record<DayOutcome, { label: string; className: string }> = {
  won: { label: 'Won ✓', className: 'bg-emerald-100 text-emerald-800' },
  lost: { label: 'Lost ✗', className: 'bg-red-100 text-red-700' },
  in_progress: { label: 'In progress…', className: 'bg-amber-100 text-amber-800' },
  not_played: { label: 'Not played', className: 'bg-stone-100 text-stone-400' },
};

/** Past days can't be resumed, so a dangling life is "Incomplete", not "In progress". */
function badgeFor(outcome: DayOutcome, date: string): { label: string; className: string } {
  if (outcome === 'in_progress' && date < londonToday()) {
    return { label: 'Incomplete', className: 'bg-stone-200 text-stone-600' };
  }
  return OUTCOME_BADGES[outcome];
}

const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  EASY: 'Easy',
  MEDIUM: 'Medium',
  HARD: 'Hard',
};

function bestScore(entries: HistoryEntry[]): number {
  return entries.reduce((m, e) => Math.max(m, e.final_score), 0);
}

export default function HistoryPlays() {
  const { user, loading } = useAuth();
  const [filter, setFilter] = useState<Difficulty | 'ALL'>('ALL');
  // null = default (most recent day open); becomes a Set once the user toggles.
  const [expandedOverride, setExpandedOverride] = useState<Set<string> | null>(null);

  const historyQuery = useInfiniteQuery({
    queryKey: ['me', 'history', filter],
    queryFn: ({ pageParam }) =>
      fetchHistory({
        page: pageParam,
        page_size: PAGE_SIZE,
        ...(filter === 'ALL' ? {} : { difficulty: filter }),
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.page_size < last.total ? last.page + 1 : undefined,
    enabled: !!user,
  });

  const entries = useMemo(
    () => historyQuery.data?.pages.flatMap((p) => p.entries) ?? [],
    [historyQuery.data],
  );

  // Group by date, newest first. While more pages exist, the oldest loaded
  // day may be only partially fetched — hold it back until its page lands.
  const byDate = useMemo(() => {
    const map = new Map<string, HistoryEntry[]>();
    for (const e of entries) {
      const list = map.get(e.puzzle_date) ?? [];
      list.push(e);
      map.set(e.puzzle_date, list);
    }
    const days = [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
    if (historyQuery.hasNextPage && days.length > 1) days.pop();
    return days;
  }, [entries, historyQuery.hasNextPage]);

  const expanded = expandedOverride ?? new Set(byDate.slice(0, 1).map(([d]) => d));

  function toggleDay(date: string) {
    const next = new Set(expanded);
    if (next.has(date)) next.delete(date);
    else next.add(date);
    setExpandedOverride(next);
  }

  if (!loading && !user) {
    return (
      <section data-testid="page-history-plays">
        <h1 className="text-2xl font-bold">All plays</h1>
        <p className="mt-4 text-stone-600">
          <Link to="/login" className="font-semibold text-emerald-700 underline">
            Sign in
          </Link>{' '}
          to keep a history of your daily games.
        </p>
      </section>
    );
  }

  const visibleDifficulties = filter === 'ALL' ? DIFFICULTIES : [filter];

  return (
    <section data-testid="page-history-plays">
      <Link to="/history" className="text-sm text-emerald-700 hover:underline">
        <span aria-hidden="true">←</span> Overview
      </Link>
      <h1 className="mt-1 text-2xl font-bold">All plays</h1>

      <div role="group" aria-label="Filter by difficulty" className="mt-4 flex gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            aria-pressed={filter === f.key}
            onClick={() => {
              setFilter(f.key);
              setExpandedOverride(null);
            }}
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              filter === f.key
                ? 'bg-emerald-700 text-white'
                : 'border border-stone-300 text-stone-600 hover:bg-stone-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {historyQuery.isLoading ? (
        <p role="status" className="mt-6 text-stone-500">
          Loading history…
        </p>
      ) : byDate.length === 0 ? (
        <p className="mt-6 text-stone-600">
          {filter === 'ALL' ? (
            <>
              Nothing here yet — play{' '}
              <Link to="/" className="text-emerald-700 underline">
                today's puzzles
              </Link>
              .
            </>
          ) : (
            `No ${DIFFICULTY_LABELS[filter as Difficulty]} games yet.`
          )}
        </p>
      ) : (
        <>
          <ul className="mt-4 space-y-3">
            {byDate.map(([date, dayEntries]) => {
              const isOpen = expanded.has(date);
              return (
                <li
                  key={date}
                  data-testid={`history-day-${date}`}
                  className="rounded-lg border border-stone-200 bg-white"
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => toggleDay(date)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-stone-50"
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{formatDateUK(date)}</span>
                      {/* Collapsed highlight: one chip per played difficulty. */}
                      {!isOpen
                        ? visibleDifficulties.map((difficulty) => {
                            const own = dayEntries.filter((e) => e.difficulty === difficulty);
                            if (own.length === 0) return null;
                            const outcome = dayOutcome(own);
                            const badge = badgeFor(outcome, date);
                            // Won/lost compress to their icon; unresolved
                            // states keep the full word ("In progress…" /
                            // "Incomplete") so they read unambiguously.
                            const status =
                              outcome === 'won' ? '✓' : outcome === 'lost' ? '✗' : badge.label;
                            return (
                              <span
                                key={difficulty}
                                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}
                              >
                                {DIFFICULTY_LABELS[difficulty]} {status} {bestScore(own)}
                              </span>
                            );
                          })
                        : null}
                    </span>
                    <span aria-hidden="true" className="shrink-0 text-stone-400">
                      {isOpen ? '▾' : '▸'}
                    </span>
                  </button>

                  {isOpen ? (
                    <div className="divide-y divide-stone-100 border-t border-stone-100 px-4 pb-2">
                      {visibleDifficulties.map((difficulty) => {
                        const own = dayEntries.filter((e) => e.difficulty === difficulty);
                        const outcome = dayOutcome(own);
                        const badge = badgeFor(outcome, date);
                        const ariaStatus =
                          badge.label === 'Incomplete' ? 'incomplete' : outcome.replace('_', ' ');
                        const summary = (
                          <span className="flex items-center gap-2.5">
                            <span className="w-16 text-sm font-medium">
                              {DIFFICULTY_LABELS[difficulty]}
                            </span>
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                            {own.length > 0 ? (
                              <span className="text-sm text-stone-500">{bestScore(own)} pts</span>
                            ) : null}
                          </span>
                        );
                        return own.length > 0 ? (
                          <Link
                            key={difficulty}
                            to={`/history/${date}/${difficulty.toLowerCase()}`}
                            aria-label={`${difficulty} on ${date}: ${ariaStatus} — view board`}
                            className="group -mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-2.5 hover:bg-stone-50"
                          >
                            {summary}
                            <span className="shrink-0 text-sm font-medium text-emerald-700 group-hover:underline">
                              View board <span aria-hidden="true">→</span>
                            </span>
                          </Link>
                        ) : (
                          <div
                            key={difficulty}
                            aria-label={`${difficulty} on ${date}: not played`}
                            className="flex items-center py-2.5"
                          >
                            {summary}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {historyQuery.hasNextPage ? (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => historyQuery.fetchNextPage()}
                disabled={historyQuery.isFetchingNextPage}
                className="rounded-md border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              >
                {historyQuery.isFetchingNextPage ? 'Loading…' : 'Load more days'}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
